import React, { useEffect, useState } from 'react';
import { getShops, getShop, getApiError, updateShop } from '../../../utils/api';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Checkbox,
  Button,
  Alert,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Divider
} from '@mui/material';

const initialForm = {
  name: '',
  category: '',
  contactName: '',
  contactPhone: [],
  contactEmail: '',
  address: '',
  city: '',
  state: '',
  pinCode: '',
  gstNumber: '',
  gstType: '',
  country: '',
  googleReviewId: '',
  enableFirstPartyName: false,
  supportsAdvertisements: false,
};

export default function BusinessDetailsAdmin() {
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadShops = React.useCallback(async () => {
    try {
      setLoadingMore(true);
      const res = await getShops(page, 20);
      const newShops = res.data?.results || [];
      setShops(prev => page === 1 ? newShops : [...prev, ...newShops]);
      setHasMore(newShops.length === 20);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoadingMore(false);
    }
  }, [page]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    loadShops().finally(() => setLoading(false));
  }, [loadShops]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
    loadShops();
  };

  // Fetch selected shop details
  useEffect(() => {
    if (!selectedShopId) return;
    setLoading(true);
    getShop(selectedShopId)
      .then(res => {
        setForm({
          ...initialForm,
          ...res.data,
        });
        setLoading(false);
        setError('');
      })
      .catch(err => {
        setError(getApiError(err));
        setLoading(false);
      });
  }, [selectedShopId]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: checked }));
    } else if (name === 'contactPhone') {
      setForm(f => ({ ...f, contactPhone: value.split(',').map(v => v.trim()) }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  // Save/Update shop details
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      // Only send fields that are in the payload spec
      const payload = {
        name: form.name,
        category: form.category,
        contactName: form.contactName,
        contactPhone: form.contactPhone,
        contactEmail: form.contactEmail,
        address: form.address,
        city: form.city,
        state: form.state,
        pinCode: form.pinCode,
        gstNumber: form.gstNumber,
        gstType: form.gstType,
        country: form.country,
        googleReviewId: form.googleReviewId,
        enableFirstPartyName: form.enableFirstPartyName,
        supportsAdvertisements: form.supportsAdvertisements,
      };
      await updateShop(selectedShopId, payload);
      setMessage('Business details updated successfully.');
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" align="center" mb={2}>
          Business Details Admin
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={2}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        <form onSubmit={handleSave} autoComplete="off">
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="select-business-label">Select Business</InputLabel>
            <Select
              labelId="select-business-label"
              value={selectedShopId}
              label="Select Business"
              onChange={e => setSelectedShopId(e.target.value)}
              disabled={loading}
            >
              <MenuItem value=''>-- Select --</MenuItem>
              {shops.map(shop => (
                <MenuItem key={shop.id} value={shop.id}>
                  {shop.name} ({shop.city}, {shop.state})
                </MenuItem>
              ))}
              {hasMore && !loadingMore && (
                <MenuItem onClick={handleLoadMore} sx={{ justifyContent: 'center', color: 'primary.main' }}>
                  Load More Businesses...
                </MenuItem>
              )}
              {loadingMore && (
                <MenuItem sx={{ justifyContent: 'center' }}>
                  <CircularProgress size={20} />
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {selectedShopId && !loading && (
            <>
              <Typography variant="h6" color="text.primary" fontWeight={600} mb={2}>
                Business Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Name" name="name" value={form.name} onChange={handleChange} fullWidth required size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Category" name="category" value={form.category} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Contact Name" name="contactName" value={form.contactName} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Contact Phone (comma separated)" name="contactPhone" value={form.contactPhone.join(', ')} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Contact Email" name="contactEmail" value={form.contactEmail} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="State" name="state" value={form.state} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Pin Code" name="pinCode" value={form.pinCode} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="GST Number" name="gstNumber" value={form.gstNumber} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="GST Type" name="gstType" value={form.gstType} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Country" name="country" value={form.country} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Google Review ID" name="googleReviewId" value={form.googleReviewId} onChange={handleChange} fullWidth size="small" sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="enableFirstPartyName"
                          checked={form.enableFirstPartyName}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label={<Typography fontWeight={500}>Enable First Party Name</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="supportsAdvertisements"
                          checked={form.supportsAdvertisements}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label={<Typography fontWeight={500}>Supports Advertisements</Typography>}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 1, fontWeight: 700 }}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </form>
      </Paper>
    </Box>
  );
} 
